/*
 Replaces [[widget:...]] tags in from the string
 */

export enum WidgetType {
  REFILL = 'refill',
  REFILL_RESPONSE = 'refill-response',
  WL_FILE_UPLOAD = 'wl-file-upload',
}

export type WidgetPermission = 'clinician' | 'patient';

export interface WidgetConfig {
  permissions: WidgetPermission[];
  enabled?: boolean;
  previewToken?: boolean;
}

export type WidgetConfigMap = Record<WidgetType, WidgetConfig>;

interface WidgetMatch {
  fullMatch: string;
  widgetName: string;
  rawObject: string | null;
}

export function replaceWidgetsInContent(
  content: string,
  recipientId: number,
  widgetsConfig: Partial<WidgetConfigMap>,
  messageId: number,
  app: string,
): string {
  const widgets = extractWidgetParts(content);
  const isPatientMessage = recipientId === -1;

  for (const { fullMatch, widgetName, rawObject } of widgets) {
    const validWidget = Object.values(WidgetType).includes(widgetName as WidgetType);

    if (!validWidget) {
      return content;
    }

    const config = widgetsConfig[widgetName as WidgetType];

    if (!config || config.enabled === false) {
      content = app === 'pims' ? content.replace(fullMatch, `[disabled widget: ${widgetName}]`) : content;
      continue;
    }

    const canRender =
      (isPatientMessage && config.permissions.includes('patient')) ||
      (!isPatientMessage && config.permissions.includes('clinician'));

    if (canRender) {
      const dataObj = rawObject ? parseWidgetObject(rawObject) : {};
      const replacement = config.previewToken
        ? replaceWidgetWithToken(fullMatch)
        : buildWidgetTag(widgetName as WidgetType, dataObj, messageId);
      content = content.replace(fullMatch, replacement);
    }
  }

  return content;
}

export function parseWidgetObject(rawStr: string): any {
  try {
    let jsonStr = rawStr.trim();
    jsonStr = decodeHtmlEntitiesSimple(jsonStr);
    jsonStr = quoteAllKeys(jsonStr);
    jsonStr = quoteUnquotedValues(jsonStr);
    jsonStr = normalizeSingleQuotes(jsonStr);
    jsonStr = jsonStr.replace(/,(\s*[}\]])/g, '$1'); // remove trailing commas
    return JSON.parse(jsonStr);
  } catch (err) {
    console.warn('Failed to parse widget object:', rawStr, err);
    return { error: 'Unable to parse widget object.\n\nRaw input:\n\n' + rawStr };
  }
}

function decodeHtmlEntitiesSimple(str: string): string {
  return str
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>');
}

function quoteAllKeys(str: string): string {
  return str.replace(/([{,]\s*)([a-zA-Z_]\w*|\d+)(\s*:)/g, '$1"$2"$3');
}

function quoteUnquotedValues(str: string): string {
  return str.replace(/:\s*([^:]+?)(?=\s*(?:,\s*(?:"[^"]*"|[a-zA-Z_][a-zA-Z0-9_]*)\s*:|[}]))/g, (match, value) => {
    const trimmed = value.trim().replace(/,$/, '');
    if ((trimmed.startsWith('"') && trimmed.endsWith('"')) || (trimmed.startsWith("'") && trimmed.endsWith("'"))) {
      return `: ${trimmed}`;
    }
    if (trimmed.startsWith('{')) return `: ${trimmed}`;
    if (/^-?\d+(\.\d+)?$/.test(trimmed)) return `: ${trimmed}`;
    if (['true', 'false', 'null'].includes(trimmed)) return `: ${trimmed}`;
    return `: "${trimmed}"`;
  });
}

function normalizeSingleQuotes(str: string): string {
  let inDoubleQuotes = false;
  let result = '';

  for (let i = 0; i < str.length; i++) {
    const char = str[i];
    const prevChar = i > 0 ? str[i - 1] : '';

    if (char === '"' && prevChar !== '\\') {
      inDoubleQuotes = !inDoubleQuotes;
      result += char;
    } else if (char === "'" && !inDoubleQuotes) {
      result += '"';
    } else {
      result += char;
    }
  }
  return result;
}

function extractWidgetParts(str: string): WidgetMatch[] {
  // Updated regex to make the object part optional
  const regex = /\[\[widget:([a-zA-Z0-9_-]+)(?::(\{[\s\S]*?\}))?\]\]/g;
  const matches: WidgetMatch[] = [];
  let match: RegExpExecArray | null;

  while ((match = regex.exec(str)) !== null) {
    matches.push({
      fullMatch: match[0],
      widgetName: match[1],
      rawObject: match[2] || null,
    });
  }

  return matches;
}

function buildWidgetTag(widgetName: string, dataObj: any, messageId?: number): string {
  if (dataObj?.error) {
    return dataObj.error;
  }

  const payload = typeof messageId === 'number' ? { ...dataObj, message_id: messageId } : dataObj;

  const json = JSON.stringify(payload);

  return `<lib-widget-${widgetName} [data]='${json}'></lib-widget-${widgetName}>`;
}

function replaceWidgetWithToken(str: string): string {
  const widgetRegex = /\[\[widget:([a-zA-Z0-9_-]+)(?::(\{[\s\S]*?\}))?\]\]/g;
  return str.replace(widgetRegex, (full, widgetName, objectContent) => {
    if (!objectContent) {
      return `{${widgetName.toUpperCase()}_WIDGET}`;
    }

    const serviceCodeMatch = objectContent.match(/service_code\s*:\s*(?:'([^']*)'|"([^"]*)"|([A-Za-z0-9_-]+))/i);

    if (!serviceCodeMatch) {
      return `{${widgetName.toUpperCase()}_WIDGET}`;
    }

    const serviceCode = serviceCodeMatch[1] || serviceCodeMatch[2] || serviceCodeMatch[3];
    const prefix = serviceCode.split(/[_-]/)[0];
    return `{${prefix.toUpperCase()}_${widgetName.toUpperCase()}}`;
  });
}
